import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, forkJoin, tap } from 'rxjs';
import { concatMap, map } from 'rxjs/operators';
import { Partner, PartnerCreate, PartnerUpdate } from '@core/entities/models/partner.model';
import { PaginatedList } from '@core/entities/models/paginated-list.model';
import { ApiEndpoints } from '@core/config/api.config';
import { skipGlobalLoading } from '@core/interceptors/loading-context';

/**
 * Parámetros de filtrado para listar partners según OpenAPI.
 */
export interface PartnerListParams {
  offset?: number;
  limit?: number;
  /** Filtrar por país */
  countryId?: string;
  /** Filtro de búsqueda (max 128 chars) */
  filter?: string;
  /** Campo de ordenación según OpenAPI */
  sortBy?: 'name' | 'createdAt' | 'updatedAt' | 'counts.beneficiaries';
  sortOrder?: 'ASC' | 'DESC';
}

/**
 * Servicio para gestionar partners.
 * Implementa las operaciones CRUD según OpenAPI spec.
 */
@Injectable({ providedIn: 'root' })
export class PartnerService {
  private readonly http = inject(HttpClient);

  // ---------------------------------------------------------------------------
  // API pública - Operaciones CRUD de partners
  // ---------------------------------------------------------------------------

  /**
   * Obtiene un listado paginado de partners.
   * GET /partners (ListPartnerOperation)
   *
   * @param params - Parámetros de paginación y filtrado (offset, limit, countryId)
   * @returns Observable con el listado paginado de partners
   */
  getAll(params?: PartnerListParams): Observable<PaginatedList<Partner>> {
    let httpParams = new HttpParams();

    httpParams = httpParams.set('offset', (params?.offset ?? 0).toString());
    httpParams = httpParams.set('limit', (params?.limit ?? 10).toString());

    if (params?.countryId) {
      httpParams = httpParams.set('countryId', params.countryId);
    }
    if (params?.filter) {
      httpParams = httpParams.set('filter', params.filter);
    }
    if (params?.sortBy) {
      httpParams = httpParams.set('sortBy', params.sortBy);
    }
    if (params?.sortOrder) {
      httpParams = httpParams.set('sortOrder', params.sortOrder);
    }

    return this.http
      .get<PaginatedList<Partner>>(ApiEndpoints.partners.base, {
        params: httpParams,
        context: skipGlobalLoading(),
      })
      .pipe(
        tap({
          error: (error: any) => console.error('❌ Partners Error:', error),
        }),
      );
  }

  /**
   * Obtiene un partner por su ID.
   * GET /partners/{partnerId} (GetPartnerOperation)
   *
   * @param id - Identificador único del partner
   * @returns Observable con el partner solicitado
   */
  getById(id: string): Observable<Partner> {
    return this.http.get<Partner>(ApiEndpoints.partners.byId(id));
  }

  /**
   * Crea un nuevo partner.
   * POST /partners (CreatePartnerOperation)
   *
   * @param partner - Datos del partner a crear
   * @returns Observable con el partner creado
   */
  create(partner: PartnerCreate): Observable<Partner> {
    return this.http.post<Partner>(ApiEndpoints.partners.base, partner);
  }

  /**
   * Crea un nuevo partner dentro de un country.
   * POST /countries/{countryId}/partners
   *
   * @param countryId - ID del country donde crear el partner
   * @param data - Datos del partner a crear (solo name)
   * @returns Observable con el partner creado
   */
  createInCountry(countryId: string, data: { name: string }): Observable<Partner> {
    return this.http.post<Partner>(ApiEndpoints.partners.createInCountry(countryId), data);
  }

  /**
   * Actualiza un partner existente.
   * PUT /partners/{partnerId} (UpdatePartnerOperation)
   *
   * @param id - Identificador del partner a actualizar
   * @param partner - Datos actualizados del partner
   * @returns Observable con el partner actualizado
   */
  update(id: string, partner: PartnerUpdate): Observable<Partner> {
    return this.http.put<Partner>(ApiEndpoints.partners.byId(id), partner);
  }

  /**
   * Elimina un partner.
   * DELETE /partners/{partnerId} (DeletePartnerOperation)
   *
   * @param id - Identificador del partner a eliminar
   * @returns Observable vacío cuando el partner se elimina correctamente
   */
  delete(id: string): Observable<void> {
    return this.http.delete<void>(ApiEndpoints.partners.byId(id));
  }

  /**
   * Obtiene todos los partners para exportación (hace múltiples llamadas hasta obtener todos)
   */
  getAllForExport(params?: PartnerListParams): Observable<Partner[]> {
    return this.getAll({ ...params, offset: 0, limit: 100 }).pipe(
      concatMap((firstResponse) => {
        const total = firstResponse.total;

        if (total <= 100 || total === 0) {
          return of(firstResponse.items);
        }

        const totalPages = Math.ceil(total / 100);
        const requests: Observable<PaginatedList<Partner>>[] = [of(firstResponse)];

        for (let page = 1; page < totalPages; page++) {
          const offset = page * 100;
          requests.push(this.getAll({ ...params, offset, limit: 100 }));
        }

        return forkJoin(requests).pipe(
          map((responses) => {
            const allPartners: Partner[] = [];
            responses.forEach((response) => {
              allPartners.push(...response.items);
            });
            return allPartners;
          }),
        );
      }),
    );
  }
}
